/**
 * DateFilter — per-column date filter input.
 *
 * Round 4 of the per-filter design-system migration. Replaces the
 * legacy `_legacy/components/FormikFields/Calendar/CalendarPopover`
 * delegation with a thin wrapper around `@shared/ui/DateField`.
 * Same react-date-range engine underneath; the difference is the
 * trigger now matches the rest of the inline filter row (size sm,
 * brand-highlighted when filled, design-system tokens) and the
 * popover renders inside our shared `<Popover>` shell instead of
 * legacy MUI's.
 *
 * Behaviour preserved 1:1 vs. legacy:
 *   - `shouldRange` toggles between single-day and range pickers.
 *   - Single-mode value is `MM/DD/YYYY`; range value is
 *     `MM/DD/YYYY,MM/DD/YYYY` (start,end joined by comma).
 *   - `hasNoMaxDate` removes the today-as-maxDate cap (used by
 *     columns that allow scheduling future dates).
 *   - `isFilterable: false` disables the trigger.
 */
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DateField } from '@shared/ui/DateField';

const DateFilter = ({
  filterValue = '',
  dataKey,
  columnData: { isFilterable, shouldRange, hasNoMaxDate },
  onChange,
}) => {
  const { tables } = useSelector(s => s.i18n);

  const handleChange = next => {
    onChange(dataKey, next || '');
  };

  return (
    <DateField
      size='sm'
      fullWidth
      range={!!shouldRange}
      highlighted={!!filterValue}
      value={filterValue || ''}
      onChange={handleChange}
      disabled={!isFilterable}
      maxDate={hasNoMaxDate ? undefined : new Date()}
      placeholder={tables?.filter || ''}
    />
  );
};

DateFilter.propTypes = {
  filterValue: PropTypes.string,
  dataKey: PropTypes.string.isRequired,
  columnData: PropTypes.shape({
    isFilterable: PropTypes.bool,
    shouldRange: PropTypes.bool,
    hasNoMaxDate: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func,
};

export default DateFilter;
